import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Camera, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import Navigation from '@/components/site/Navigation';
import Footer from '@/components/site/Footer';

const CATEGORIES = [
  'Television', 'Radio', 'Newspaper or Magazine', 'Digital Publication', 'Podcast',
  'Blog', 'Photographer', 'Videographer', 'Social Media Creator', 'Freelance Journalist', 'Other',
];

const Section = ({ title, children }) => (
  <Card className="p-6 md:p-8 rounded-2xl border border-aawta-border shadow-aawta-soft space-y-4">
    <h3 className="font-display font-semibold text-xl text-aawta-navy">{title}</h3>
    <div className="space-y-4">{children}</div>
  </Card>
);
const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[13.5px] font-medium text-aawta-navy">
      {label} {required && <span className="text-aawta-magenta">*</span>}
    </Label>
    {children}
  </div>
);

const Media = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ credentials_requested: true, agree_media_policy: false });
  const [logo, setLogo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === 'string' || typeof e === 'boolean' ? e : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) return toast.error('Name and email are required.');
    if (!logo) return toast.error('Please upload your media/company logo.');
    if (!form.agree_media_policy) return toast.error('Please agree to the media & credentialing policy.');
    setSubmitting(true);
    try {
      let logoId = null;
      if (logo) {
        const fd = new FormData();
        fd.append('file', logo);
        fd.append('folder', 'logos');
        const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        logoId = data.id;
      }
      await api.post('/media', {
        ...form,
        representatives_count: form.representatives_count ? Number(form.representatives_count) : null,
        logo_file_id: logoId,
      });
      setDone(true); window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <main className="pt-32 pb-24">
          <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center" data-testid="media-thank-you">
            <div className="w-16 h-16 rounded-full bg-aawta-magenta/10 text-aawta-magenta mx-auto flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-6 text-aawta-navy">Thank You!</h1>
            <div className="divider-magenta mt-6 mx-auto" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              Your media credential application has been received. The AAWTA team will review
              and follow up with credentialing details, media kit access, and event logistics.
            </p>
            <Button onClick={() => navigate('/')} className="mt-8 rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white px-7 py-6">Back to Home</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aawta-surface">
      <Navigation />
      <main className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-aawta-muted hover:text-aawta-navy text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </button>
          <div className="mt-6 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-aawta-magenta text-white flex items-center justify-center shrink-0"><Camera className="h-5 w-5" /></div>
            <div>
              <span className="overline text-aawta-magenta">Media Application</span>
              <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 text-aawta-navy leading-[1.05]">Apply for Media Credentials</h1>
            </div>
          </div>
          <div className="divider-magenta mt-6" />
          <p className="mt-6 text-[16px] leading-relaxed text-aawta-muted">
            Apply to cover the Building the Future Workforce Summit. Our team will follow up
            with credentialing, interview requests, and press kit access.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <Section title="Contact">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name" required><Input required data-testid="media-name" value={form.full_name || ''} onChange={set('full_name')} className="rounded-lg" /></Field>
                <Field label="Email" required><Input type="email" required data-testid="media-email" value={form.email || ''} onChange={set('email')} className="rounded-lg" /></Field>
                <Field label="Phone"><Input data-testid="media-phone" value={form.phone || ''} onChange={set('phone')} className="rounded-lg" /></Field>
                <Field label="Media outlet / organization"><Input data-testid="media-outlet" value={form.outlet || ''} onChange={set('outlet')} className="rounded-lg" /></Field>
                <Field label="Job title / media role"><Input data-testid="media-title" value={form.job_title || ''} onChange={set('job_title')} className="rounded-lg" /></Field>
                <Field label="Website"><Input data-testid="media-website" value={form.website || ''} onChange={set('website')} placeholder="https://" className="rounded-lg" /></Field>
              </div>
              <Field label="Social media links"><Textarea data-testid="media-social" rows={2} value={form.social_media || ''} onChange={set('social_media')} className="rounded-lg resize-none" placeholder="Instagram, X/Twitter, LinkedIn, TikTok…" /></Field>
            </Section>

            <Section title="Coverage">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Type of media coverage">
                  <select data-testid="media-category" value={form.coverage_type || ''} onChange={set('coverage_type')} className="w-full h-10 rounded-lg border border-aawta-border bg-white px-3 text-sm">
                    <option value="">Select category…</option>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Estimated audience or reach"><Input data-testid="media-reach" value={form.estimated_reach || ''} onChange={set('estimated_reach')} placeholder="e.g. 50,000 monthly readers" className="rounded-lg" /></Field>
                <Field label="Number of media representatives attending"><Input type="number" min="1" max="50" data-testid="media-reps" value={form.representatives_count || ''} onChange={set('representatives_count')} className="rounded-lg" /></Field>
                <Field label="Equipment being brought"><Input data-testid="media-equipment" value={form.equipment || ''} onChange={set('equipment')} placeholder="Cameras, tripods, lights, mics…" className="rounded-lg" /></Field>
              </div>
              <Field label="Coverage plans"><Textarea data-testid="media-plans" rows={3} value={form.coverage_plans || ''} onChange={set('coverage_plans')} className="rounded-lg resize-none" /></Field>
              <Field label="Interview requests"><Textarea data-testid="media-interviews" rows={2} value={form.interview_requests || ''} onChange={set('interview_requests')} className="rounded-lg resize-none" placeholder="Speakers or partners you'd like to interview" /></Field>
              <Field label="Media / company logo" required>
                <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-aawta-border bg-aawta-surface px-4 py-3 hover:border-aawta-magenta/50" data-testid="media-logo-input">
                  <Upload className="h-5 w-5 text-aawta-magenta shrink-0" />
                  <span className="text-[14px] text-aawta-navy font-medium truncate">{logo?.name || 'Upload media / company logo (PNG, JPG or JPEG)'}</span>
                  <input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" className="hidden" onChange={(e) => setLogo(e.target.files?.[0] || null)} />
                </label>
                <div className="text-[12px] text-aawta-muted mt-1">Please provide a transparent PNG or high-resolution logo. Used for the public “Meet Our Approved Media” section once approved.</div>
              </Field>
              <Field label="Additional notes"><Textarea data-testid="media-notes" rows={2} value={form.additional_notes || ''} onChange={set('additional_notes')} className="rounded-lg resize-none" /></Field>
              <label className="flex items-start gap-3">
                <Checkbox checked={!!form.credentials_requested} onCheckedChange={(v) => setForm((f) => ({ ...f, credentials_requested: !!v }))} className="mt-0.5 border-aawta-navy data-[state=checked]:bg-aawta-magenta data-[state=checked]:border-aawta-magenta" />
                <span className="text-[13.5px] text-aawta-muted leading-snug">I would like to request media credentials for the Summit.</span>
              </label>
              <label className="flex items-start gap-3">
                <Checkbox checked={!!form.agree_media_policy} onCheckedChange={(v) => setForm((f) => ({ ...f, agree_media_policy: !!v }))} data-testid="media-policy" className="mt-0.5 border-aawta-navy data-[state=checked]:bg-aawta-magenta data-[state=checked]:border-aawta-magenta" />
                <span className="text-[13.5px] text-aawta-muted leading-snug">I agree to follow the AAWTA Summit media and credentialing policies. *</span>
              </label>
            </Section>

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={submitting} data-testid="media-submit" className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white px-8 py-6 font-medium shadow-aawta-magenta">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</> : 'Submit Media Application'}
              </Button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Media;
